/**
 * Federal tax engine — 1040 individual + 1120 C-corp.
 *
 * Pass-through entities (1120-S, 1065) compute taxable income at the entity
 * level but tax is paid by owners on their 1040 — so the engine models that
 * by piping K-1 ordinary / capital flows into a 1040 input.
 *
 * Brackets and thresholds are data-driven by tax year. Verify against
 * Rev. Proc. annually before relying on numbers for client returns.
 *
 * 2025 figures sourced from Rev. Proc. 2024-40. 2026 figures should be
 * updated from Rev. Proc. 2025-XX when released — placeholder uses 2025
 * scaled by an assumed ~2.7% COLA (mark as estimate in UI).
 */

export const FILING_STATUSES = [
  { id: "single", label: "Single" },
  { id: "mfj", label: "Married Filing Jointly" },
  { id: "mfs", label: "Married Filing Separately" },
  { id: "hoh", label: "Head of Household" },
];

// ─── 2024 (Rev. Proc. 2023-34) ─────────────────────────────────────────
const Y2024 = {
  year: 2024,
  source: "Rev. Proc. 2023-34",
  brackets: {
    single: [
      { from: 0, rate: 0.1 },
      { from: 11600, rate: 0.12 },
      { from: 47150, rate: 0.22 },
      { from: 100525, rate: 0.24 },
      { from: 191950, rate: 0.32 },
      { from: 243725, rate: 0.35 },
      { from: 609350, rate: 0.37 },
    ],
    mfj: [
      { from: 0, rate: 0.1 },
      { from: 23200, rate: 0.12 },
      { from: 94300, rate: 0.22 },
      { from: 201050, rate: 0.24 },
      { from: 383900, rate: 0.32 },
      { from: 487450, rate: 0.35 },
      { from: 731200, rate: 0.37 },
    ],
    mfs: [
      { from: 0, rate: 0.1 },
      { from: 11600, rate: 0.12 },
      { from: 47150, rate: 0.22 },
      { from: 100525, rate: 0.24 },
      { from: 191950, rate: 0.32 },
      { from: 243725, rate: 0.35 },
      { from: 365600, rate: 0.37 },
    ],
    hoh: [
      { from: 0, rate: 0.1 },
      { from: 16550, rate: 0.12 },
      { from: 63100, rate: 0.22 },
      { from: 100500, rate: 0.24 },
      { from: 191950, rate: 0.32 },
      { from: 243700, rate: 0.35 },
      { from: 609350, rate: 0.37 },
    ],
  },
  stdDeduction: {
    single: 14600,
    mfj: 29200,
    mfs: 14600,
    hoh: 21900,
  },
  ltcgBreakpoints: {
    single: { fifteen: 47025, twenty: 518900 },
    mfj: { fifteen: 94050, twenty: 583750 },
    mfs: { fifteen: 47025, twenty: 291850 },
    hoh: { fifteen: 63000, twenty: 551350 },
  },
  niit: {
    rate: 0.038,
    threshold: { single: 200000, mfj: 250000, mfs: 125000, hoh: 200000 },
  },
  qbi: {
    rate: 0.2,
    incomeLimit: { single: 191950, mfj: 383900, mfs: 191950, hoh: 191950 },
    phaseInRange: { single: 50000, mfj: 100000, mfs: 50000, hoh: 50000 },
  },
  se: {
    socialSecurityWageBase: 168600,
    socialSecurityRate: 0.124,
    medicareRate: 0.029,
    additionalMedicareRate: 0.009,
    additionalMedicareThreshold: {
      single: 200000,
      mfj: 250000,
      mfs: 125000,
      hoh: 200000,
    },
    seBaseFactor: 0.9235,
  },
  amt: {
    exemption: {
      single: 85700,
      mfj: 133300,
      mfs: 66650,
      hoh: 85700,
    },
    phaseoutStart: {
      single: 609350,
      mfj: 1218700,
      mfs: 609350,
      hoh: 609350,
    },
    phaseoutRate: 0.25,
    rateBreakpoint: {
      single: 232600,
      mfj: 232600,
      mfs: 116300,
      hoh: 232600,
    },
    lowerRate: 0.26,
    upperRate: 0.28,
  },
  // Child Tax Credit + Credit for Other Dependents (statutory §24)
  ctc: {
    perChild: 2000,
    perOtherDep: 500,
    phaseoutStart: { single: 200000, mfj: 400000, mfs: 200000, hoh: 200000 },
    phaseoutPerThousand: 50,
  },
  cCorpRate: 0.21,
};

// ─── 2025 baseline (Rev. Proc. 2024-40) ────────────────────────────────
const Y2025 = {
  year: 2025,
  source: "Rev. Proc. 2024-40",
  brackets: {
    single: [
      { from: 0, rate: 0.1 },
      { from: 11925, rate: 0.12 },
      { from: 48475, rate: 0.22 },
      { from: 103350, rate: 0.24 },
      { from: 197300, rate: 0.32 },
      { from: 250525, rate: 0.35 },
      { from: 626350, rate: 0.37 },
    ],
    mfj: [
      { from: 0, rate: 0.1 },
      { from: 23850, rate: 0.12 },
      { from: 96950, rate: 0.22 },
      { from: 206700, rate: 0.24 },
      { from: 394600, rate: 0.32 },
      { from: 501050, rate: 0.35 },
      { from: 751600, rate: 0.37 },
    ],
    mfs: [
      { from: 0, rate: 0.1 },
      { from: 11925, rate: 0.12 },
      { from: 48475, rate: 0.22 },
      { from: 103350, rate: 0.24 },
      { from: 197300, rate: 0.32 },
      { from: 250525, rate: 0.35 },
      { from: 375800, rate: 0.37 },
    ],
    hoh: [
      { from: 0, rate: 0.1 },
      { from: 17000, rate: 0.12 },
      { from: 64850, rate: 0.22 },
      { from: 103350, rate: 0.24 },
      { from: 197300, rate: 0.32 },
      { from: 250500, rate: 0.35 },
      { from: 626350, rate: 0.37 },
    ],
  },
  stdDeduction: {
    single: 15000,
    mfj: 30000,
    mfs: 15000,
    hoh: 22500,
  },
  ltcgBreakpoints: {
    single: { fifteen: 48350, twenty: 533400 },
    mfj: { fifteen: 96700, twenty: 600050 },
    mfs: { fifteen: 48350, twenty: 300000 },
    hoh: { fifteen: 64750, twenty: 566700 },
  },
  niit: {
    rate: 0.038,
    threshold: { single: 200000, mfj: 250000, mfs: 125000, hoh: 200000 },
  },
  qbi: {
    rate: 0.2,
    incomeLimit: { single: 197300, mfj: 394600, mfs: 197300, hoh: 197300 },
    phaseInRange: { single: 50000, mfj: 100000, mfs: 50000, hoh: 50000 },
  },
  se: {
    socialSecurityWageBase: 176100,
    socialSecurityRate: 0.124,
    medicareRate: 0.029,
    additionalMedicareRate: 0.009,
    additionalMedicareThreshold: {
      single: 200000,
      mfj: 250000,
      mfs: 125000,
      hoh: 200000,
    },
    seBaseFactor: 0.9235,
  },
  // AMT (§55) — 2025 figures, Rev. Proc. 2024-40
  amt: {
    exemption: {
      single: 88100,
      mfj: 137000,
      mfs: 68500,
      hoh: 88100,
    },
    phaseoutStart: {
      single: 626350,
      mfj: 1252700,
      mfs: 626350,
      hoh: 626350,
    },
    phaseoutRate: 0.25, // 25 cents of exemption phased out per dollar over threshold
    rateBreakpoint: {
      single: 239100,
      mfj: 239100,
      mfs: 119550,
      hoh: 239100,
    },
    lowerRate: 0.26,
    upperRate: 0.28,
  },
  // Child Tax Credit + Credit for Other Dependents (statutory §24)
  ctc: {
    perChild: 2000,
    perOtherDep: 500,
    phaseoutStart: { single: 200000, mfj: 400000, mfs: 200000, hoh: 200000 },
    phaseoutPerThousand: 50,
  },
  cCorpRate: 0.21,
};

// ─── 2026 placeholder — scale 2025 thresholds by ~2.7% COLA ─────────────
const COLA_2026 = 1.027;
function scale(obj, mul) {
  if (typeof obj === "number") return Math.round(obj * mul);
  if (Array.isArray(obj)) return obj.map((v) => scale(v, mul));
  if (obj && typeof obj === "object") {
    const out = {};
    for (const k of Object.keys(obj)) {
      // Don't scale rates or factors
      if (k === "rate" || k.endsWith("Rate") || k === "seBaseFactor") {
        out[k] = obj[k];
      } else {
        out[k] = scale(obj[k], mul);
      }
    }
    return out;
  }
  return obj;
}

const Y2026 = {
  ...scale(Y2025, COLA_2026),
  year: 2026,
  source: "Estimated — scale Y2025 by ~2.7% COLA. Verify with current Rev. Proc.",
  cCorpRate: 0.21,
  // CTC amounts and phase-out are statutory (§24), NOT COLA-adjusted.
  // Restore to 2025 values (which match statute).
  ctc: Y2025.ctc,
};

const TAX_YEARS = {
  2024: Y2024,
  2025: Y2025,
  2026: Y2026,
};

export function getFederalData(year) {
  return TAX_YEARS[year] || Y2026;
}

// ─── Bracket math ──────────────────────────────────────────────────────
export function taxFromBrackets(taxableIncome, brackets) {
  if (taxableIncome <= 0) return 0;
  let tax = 0;
  for (let i = 0; i < brackets.length; i++) {
    const cur = brackets[i];
    const next = brackets[i + 1];
    if (taxableIncome <= cur.from) break;
    const top = next ? Math.min(taxableIncome, next.from) : taxableIncome;
    tax += (top - cur.from) * cur.rate;
    if (!next || taxableIncome <= next.from) break;
  }
  return tax;
}

export function marginalRate(taxableIncome, brackets) {
  if (taxableIncome <= 0) return brackets[0].rate;
  for (let i = brackets.length - 1; i >= 0; i--) {
    if (taxableIncome > brackets[i].from) return brackets[i].rate;
  }
  return brackets[0].rate;
}

// ─── Capital gains / qualified dividends ───────────────────────────────
function capitalGainsTax(ordinaryTaxable, ltcgAmount, breakpoints) {
  if (ltcgAmount <= 0) return 0;
  const stackBase = Math.max(0, ordinaryTaxable);
  const totalIncome = stackBase + ltcgAmount;
  let tax = 0;

  // 0% bracket
  const zeroTop = breakpoints.fifteen;
  const fifteenTop = breakpoints.twenty;

  let remaining = ltcgAmount;
  let cursor = stackBase;

  // Portion in 0%
  if (cursor < zeroTop) {
    const take = Math.min(remaining, zeroTop - cursor);
    cursor += take;
    remaining -= take;
    // 0%, no tax
  }
  // Portion in 15%
  if (remaining > 0 && cursor < fifteenTop) {
    const take = Math.min(remaining, fifteenTop - cursor);
    tax += take * 0.15;
    cursor += take;
    remaining -= take;
  }
  // Portion in 20%
  if (remaining > 0) {
    tax += remaining * 0.2;
  }
  return tax;
}

// ─── Self-employment tax ───────────────────────────────────────────────
// wagesForSSCoord = W-2 wages of the SE-earner (Schedule SE Line 8a). If
// provided, subtracted from the SS wage base before the 12.4% SS portion
// applies to SE income. If 0, no coordination (matches legacy behavior).
export function selfEmploymentTax(seIncome, fed, wagesForSSCoord = 0) {
  if (seIncome <= 0) return 0;
  const base = seIncome * fed.se.seBaseFactor;
  // Schedule SE Line 9: SS wage base minus W-2 wages already subject to SS
  const remainingSSBase = Math.max(
    0,
    fed.se.socialSecurityWageBase - (parseFloat(wagesForSSCoord) || 0),
  );
  const ssBase = Math.min(base, remainingSSBase);
  const ss = ssBase * fed.se.socialSecurityRate;
  const medicare = base * fed.se.medicareRate;
  return ss + medicare;
}

// ─── NIIT ──────────────────────────────────────────────────────────────
function niit(magi, investmentIncome, status, fed) {
  const threshold = fed.niit.threshold[status];
  if (magi <= threshold || investmentIncome <= 0) return 0;
  return Math.min(investmentIncome, magi - threshold) * fed.niit.rate;
}

// ─── AMT (§55) — simplified ────────────────────────────────────────────
// AMTI = taxable income (pre-QBI add-back) + planner-entered AMT preferences
// Exemption phases out at 25¢ per $1 of AMTI over the phase-out start.
// AMT base × 26% up to breakpoint, 28% above (MFS uses half breakpoint).
// LTCG and qualified dividends inside the AMT base get their regular
// preferential rates — so we strip them out, apply 26/28% to ordinary AMT
// base only, then add LTCG/QDiv tax back at regular cap-gain rates.
// AMT due = max(0, TMT − regular tax). Limitation: no ISO lot tracking,
// no AMT-NOL, no AMT-credit (Form 8801) carryforward.
function amt({
  taxableIncome,
  preferences,
  preferentialAmt, // LTCG + qualified dividends
  regularTax,
  ordinaryTaxable,
  status,
  fed,
  ltcgBreakpoints,
}) {
  const params = fed.amt;
  if (!params) return { amti: 0, exemption: 0, tmt: 0, amtDue: 0 };
  const prefs = Math.max(0, preferences || 0);
  // AMTI — taxable income + preferences. We add QBI back too via using
  // pre-QBI taxableBeforeQBI? Simplification: use taxable income (post-QBI)
  // plus prefs. Post-TCJA QBI is allowed in computing AMTI (it's a §199A
  // deduction, not an itemized deduction subject to AMT addback).
  const amti = Math.max(0, taxableIncome + prefs);

  // Exemption with phase-out
  let exemption = params.exemption[status] || 0;
  const phaseoutStart = params.phaseoutStart[status] || 0;
  if (amti > phaseoutStart) {
    exemption = Math.max(0, exemption - (amti - phaseoutStart) * params.phaseoutRate);
  }

  const amtBase = Math.max(0, amti - exemption);
  // Strip LTCG/QDiv out of the ordinary AMT base so they get cap-gain rates
  const ordinaryAmtBase = Math.max(0, amtBase - (preferentialAmt || 0));
  const cgAmtBase = Math.max(0, Math.min(amtBase, preferentialAmt || 0));

  // Tentative minimum tax — ordinary portion at 26/28%
  const breakpoint = params.rateBreakpoint[status] || 0;
  let tmtOrdinary;
  if (ordinaryAmtBase <= breakpoint) {
    tmtOrdinary = ordinaryAmtBase * params.lowerRate;
  } else {
    // 28% on full base, less 2% on the bracket below breakpoint (shortcut)
    tmtOrdinary =
      ordinaryAmtBase * params.upperRate -
      breakpoint * (params.upperRate - params.lowerRate);
  }
  // Capital gains portion at regular LTCG rates — re-use the cap-gain
  // computation by stacking on top of the ordinary AMT base.
  const tmtCapGain = capitalGainsTax(
    ordinaryAmtBase,
    cgAmtBase,
    ltcgBreakpoints,
  );
  const tmt = tmtOrdinary + tmtCapGain;

  const amtDue = Math.max(0, tmt - (regularTax || 0));
  return { amti, exemption, tmt, amtDue };
}

// ─── QBI deduction (with W-2 / UBIA wage ceiling) ──────────────────────
// Above the income threshold (Rev. Proc. 2024-40: $394,600 MFJ / $197,300
// others), the §199A deduction per qualified trade/business is the LESSER of:
//   • 20% × QBI, OR
//   • greater of (50% × W-2 wages) and (25% × W-2 wages + 2.5% × UBIA)
// Below the threshold: full 20% × QBI applies regardless of wages/UBIA.
// Simplification: skip the phase-in range smoothing — we treat the threshold
// as a hard cliff (still directionally correct for planning, but flagged in
// the UI limitations).
function qbiDeduction(qbiIncome, taxableBeforeQBI, status, fed, wageCeiling = 0) {
  if (qbiIncome <= 0) return 0;
  const tentative = qbiIncome * fed.qbi.rate;
  const overall = Math.max(0, taxableBeforeQBI) * fed.qbi.rate;
  const threshold = fed.qbi.incomeLimit[status];
  // Below threshold: no wage/UBIA limit applies
  if (taxableBeforeQBI <= threshold) {
    return Math.min(tentative, overall);
  }
  // Above threshold: cap by wage/UBIA ceiling (entered as $ value, not %)
  // wageCeiling = sum of max(0.5*wages, 0.25*wages + 0.025*UBIA) per entity
  const wageLimited = Math.min(tentative, wageCeiling);
  return Math.min(wageLimited, overall);
}

/**
 * computeFederalTax
 *
 * input = {
 *   year: 2025|2026,
 *   filingStatus: "single"|"mfj"|"mfs"|"hoh",
 *   wages: number,           // W-2 wages
 *   interest: number,        // ordinary interest (incl. taxable bonds)
 *   ordinaryDividends: number,
 *   qualifiedDividends: number,
 *   shortTermGains: number,
 *   longTermGains: number,
 *   businessIncome: number,    // Schedule C / K-1 ordinary
 *   seIncome: number,          // subject to SE tax (sole prop, GP K-1)
 *   qbiIncome: number,         // qualifying for §199A
 *   otherIncome: number,
 *   adjustments: number,       // above-the-line (HSA, retirement, SE-tax-half, etc.)
 *   itemizedDeductions: number, // 0 = use standard
 * }
 *
 * Returns a breakdown object.
 */
export function computeFederalTax(input) {
  const fed = getFederalData(input.year);
  const status = input.filingStatus || "single";
  const brackets = fed.brackets[status];

  const wages = +input.wages || 0;
  const interest = +input.interest || 0;
  const ordDiv = +input.ordinaryDividends || 0;
  const qDiv = +input.qualifiedDividends || 0;
  const stcg = +input.shortTermGains || 0;
  const ltcg = +input.longTermGains || 0;
  const business = +input.businessIncome || 0;
  const seIncome = +input.seIncome || 0;
  const qbi = +input.qbiIncome || 0;
  const other = +input.otherIncome || 0;
  const adjustments = +input.adjustments || 0;
  const itemized = +input.itemizedDeductions || 0;

  // SE tax (one-half is an above-the-line adjustment).
  // Coordinates against the SE-earner's own W-2 wages (Schedule SE Line 8a)
  // if provided so the 12.4% SS portion doesn't double-count wages that
  // already used up the wage base at the employer.
  const wagesForSSCoord = Math.max(0, +input.seEarnerWages || 0);
  const seTax = selfEmploymentTax(seIncome, fed, wagesForSSCoord);
  const seAdjustment = seTax / 2;

  // Ordinary income — qualified dividends are NOT included in ordinary
  const ordinaryGross =
    wages + interest + ordDiv + stcg + business + other;

  // AGI (simplified)
  const agi = ordinaryGross + qDiv + ltcg - adjustments - seAdjustment;

  // Deduction
  const stdDed = fed.stdDeduction[status];
  const deduction = itemized > stdDed ? itemized : stdDed;
  const usedStandard = deduction === stdDed;

  // QBI (20% deduction, applied below the line). A planner-supplied
  // override takes precedence — used when wage/UBIA/SSTB limits or other
  // judgment calls require a different number than the simplified auto-calc.
  const taxableBeforeQBI = Math.max(0, agi - deduction);
  const qbiOverride =
    input.qbiOverride === null || input.qbiOverride === undefined
      ? null
      : Math.max(0, +input.qbiOverride || 0);
  const qbiWageCeiling = Math.max(0, +input.qbiWageCeiling || 0);
  const qbiDed =
    qbiOverride != null
      ? Math.min(qbiOverride, taxableBeforeQBI)
      : qbiDeduction(qbi, taxableBeforeQBI, status, fed, qbiWageCeiling);

  const taxableIncome = Math.max(0, taxableBeforeQBI - qbiDed);

  // Split taxable income between ordinary and preferential
  const preferentialAmt = qDiv + ltcg;
  const ordinaryTaxable = Math.max(0, taxableIncome - preferentialAmt);

  // Ordinary tax
  const ordinaryTax = taxFromBrackets(ordinaryTaxable, brackets);

  // Capital gains / qualified div tax
  const capTax = capitalGainsTax(
    ordinaryTaxable,
    Math.min(preferentialAmt, taxableIncome),
    fed.ltcgBreakpoints[status],
  );

  // NIIT — applies to investment income above MAGI threshold.
  // Planner can mark portions of LTCG as NIIT-excluded (e.g. active-trade-
  // or-business sale gain), which removes that amount from investment
  // income before NIIT is computed.
  const niitExclusion = Math.max(0, +input.niitExclusion || 0);
  const investmentIncome = Math.max(
    0,
    interest + ordDiv + qDiv + stcg + ltcg - niitExclusion,
  );
  const niitTax = niit(agi, investmentIncome, status, fed);

  // Additional Medicare tax (§3101(b)(2) / §1401(b)(2)) — 0.9% on COMBINED
  // earned income (W-2 wages + SE income × 0.9235) above the threshold.
  // Threshold: $250K MFJ / $200K Single/HoH / $125K MFS.
  // Both wages AND SE income count toward the threshold — previously only
  // SE was counted which understated the tax for high-W-2 earners.
  const addlMedicareThreshold = fed.se.additionalMedicareThreshold[status];
  const earnedIncome = wages + seIncome * fed.se.seBaseFactor;
  const addlMedicareBase = Math.max(0, earnedIncome - addlMedicareThreshold);
  const addlMedicare = addlMedicareBase * fed.se.additionalMedicareRate;

  const incomeTax = ordinaryTax + capTax;

  // Child Tax Credit ($2K per qualifying child under 17) + Credit for Other
  // Dependents ($500). Phases out $50 per $1,000 of AGI over the threshold
  // ($400K MFJ / $200K all other statuses). Applied as nonrefundable against
  // regular income tax. ACTC refundable portion (up to $1,700/child) is NOT
  // modeled — treated as capped at income tax for planning purposes.
  const kids = Math.max(0, parseInt(input.qualifyingChildren, 10) || 0);
  const otherDeps = Math.max(0, parseInt(input.otherDependents, 10) || 0);
  const ctcData = fed.ctc || {
    perChild: 2000,
    perOtherDep: 500,
    phaseoutStart: { single: 200000, mfj: 400000, mfs: 200000, hoh: 200000 },
    phaseoutPerThousand: 50,
  };
  const baseCTC =
    kids * ctcData.perChild + otherDeps * ctcData.perOtherDep;
  const ctcPhaseoutStart =
    ctcData.phaseoutStart[status] || ctcData.phaseoutStart.single;
  const ctcExcess = Math.max(0, agi - ctcPhaseoutStart);
  const ctcPhaseout = Math.ceil(ctcExcess / 1000) * ctcData.phaseoutPerThousand;
  const ctcAvailable = Math.max(0, baseCTC - ctcPhaseout);
  const ctcApplied = Math.min(ctcAvailable, incomeTax);
  const incomeTaxAfterCredits = Math.max(0, incomeTax - ctcApplied);

  // AMT — computed against the regular income tax (pre-CTC / pre-NIIT / pre-SE).
  // Returns additional tax above regular; rolls into totalTax.
  const amtPreferences = Math.max(0, +input.amtPreferences || 0);
  const amtResult = amt({
    taxableIncome,
    preferences: amtPreferences,
    preferentialAmt,
    regularTax: incomeTax,
    ordinaryTaxable,
    status,
    fed,
    ltcgBreakpoints: fed.ltcgBreakpoints[status],
  });

  const totalTax =
    incomeTaxAfterCredits + amtResult.amtDue + niitTax + seTax + addlMedicare;

  const marginal = marginalRate(ordinaryTaxable, brackets);
  const effective = agi > 0 ? totalTax / agi : 0;

  return {
    year: fed.year,
    filingStatus: status,
    agi,
    deduction,
    usedStandard,
    qbiDeduction: qbiDed,
    taxableIncome,
    ordinaryTaxable,
    preferentialIncome: preferentialAmt,
    ordinaryTax,
    capitalGainsTax: capTax,
    niitTax,
    amtTax: amtResult.amtDue,
    amti: amtResult.amti,
    amtExemption: amtResult.exemption,
    amtTMT: amtResult.tmt,
    seTax,
    additionalMedicare: addlMedicare,
    ctcAvailable,
    ctcApplied,
    ctcPhaseout,
    incomeTax,
    incomeTaxAfterCredits,
    totalTax,
    marginalRate: marginal,
    effectiveRate: effective,
    source: fed.source,
  };
}

// ─── C-corp (1120) ─────────────────────────────────────────────────────
export function computeCCorpTax(input) {
  const fed = getFederalData(input.year);
  const taxableIncome = Math.max(
    0,
    (+input.revenue || 0) -
      (+input.expenses || 0) -
      (+input.officerComp || 0) -
      (+input.depreciation || 0) -
      (+input.otherDeductions || 0),
  );
  const tax = taxableIncome * fed.cCorpRate;
  return {
    year: fed.year,
    taxableIncome,
    rate: fed.cCorpRate,
    totalTax: tax,
    effectiveRate: input.revenue > 0 ? tax / input.revenue : 0,
  };
}

export const TAX_YEAR_OPTIONS = Object.keys(TAX_YEARS).map((y) => parseInt(y, 10));

export default {
  computeFederalTax,
  computeCCorpTax,
  selfEmploymentTax,
  taxFromBrackets,
  marginalRate,
  getFederalData,
  TAX_YEAR_OPTIONS,
  FILING_STATUSES,
};
